/**
 * Second-level suffixes, so `example.co.uk` is read as an apex and offered an A
 * record rather than a CNAME it cannot legally hold.
 *
 * **A snapshot, deliberately, and the tradeoff is worth stating.** The correct
 * answer is the Public Suffix List, which is roughly fifteen thousand entries
 * and updated continuously — a real dependency to fetch, cache and keep current,
 * shipped to every browser that opens this tab. This list is the suffixes a
 * customer of a platform like this one actually turns up with.
 *
 * A suffix that is missing is not harmless, which is why the list is long rather
 * than minimal. An apex beneath an unlisted suffix reads as a subdomain, so the
 * customer is told to create `CNAME example` — and in their own zone that
 * produces `example.example.com.xx`, a record that resolves and is not the one
 * anybody asked for. An apex cannot take a CNAME at all. So the failure is wrong
 * instructions, not merely unhelpful ones, and adding a suffix here is the fix.
 */
export const MULTI_PART_TLDS = new Set([
  // United Kingdom
  "co.uk", "org.uk", "me.uk", "ac.uk", "gov.uk", "net.uk", "ltd.uk", "plc.uk",
  // Asia-Pacific
  "com.au", "net.au", "org.au", "edu.au", "id.au",
  "co.nz", "net.nz", "org.nz",
  "co.jp", "or.jp", "ne.jp", "ac.jp",
  "co.kr", "or.kr", "ne.kr",
  "co.in", "net.in", "org.in", "firm.in", "gen.in", "ind.in",
  "com.sg", "com.my", "net.my", "org.my",
  "co.id", "or.id", "web.id", "my.id", "biz.id", "ac.id", "sch.id",
  "com.ph", "com.tw", "com.hk", "com.cn", "net.cn", "org.cn",
  "com.vn", "co.th", "in.th", "com.bd", "com.pk", "com.np", "com.kh", "com.la",
  // Europe
  "com.pl", "net.pl", "org.pl", "com.tr", "com.ua", "com.ro", "com.gr",
  "com.es", "com.pt", "com.hr", "com.cy", "com.mt",
  // Middle East and Africa
  "co.il", "org.il", "net.il", "ac.il",
  "co.za", "org.za", "net.za", "web.za",
  "com.ng", "com.eg", "com.sa", "com.kw", "com.qa", "com.bh", "com.om",
  "co.ke", "co.tz", "co.ug", "com.gh", "com.ma", "com.tn",
  // Americas
  "com.br", "net.br", "org.br", "com.ar", "com.mx", "com.co", "com.pe",
  "com.ve", "com.uy", "com.ec", "com.bo", "com.py", "com.do", "com.gt",
  "com.pa", "com.sv", "com.hn", "com.ni", "com.cu",
]);

/** Apex hostnames (no subdomain) cannot use CNAME; show an A record instead. */
export function isApex(host: string): boolean {
  const labels = host.split(".");
  if (labels.length <= 2) return true;
  return labels.length === 3 && MULTI_PART_TLDS.has(labels.slice(-2).join("."));
}

/**
 * What to type in the provider's "Name" or "Host" box — the subdomain only,
 * or `@` for the apex.
 *
 * Providers ask for the name *relative to the zone*, so pasting the whole
 * hostname into a zone that already is that domain produces
 * `app.example.com.example.com`. It is the most common way a first custom
 * domain fails, and it fails silently: the record exists, it just is not the
 * one anybody asked for.
 */
export function dnsRecordName(host: string): string {
  if (isApex(host)) return "@";
  const labels = host.split(".");
  const registrable = MULTI_PART_TLDS.has(labels.slice(-2).join(".")) ? 3 : 2;
  return labels.slice(0, labels.length - registrable).join(".") || "@";
}

/**
 * The other half of a `www` pair, or null when there is no obvious partner.
 *
 * Almost nobody wants only one of `example.com` and `www.example.com` — a
 * visitor types whichever they remember, and the one that was never attached
 * fails to resolve. Offering the partner is the cheapest way to close that,
 * because attaching it is the same operation the customer just performed.
 *
 * **This pairs, it does not redirect.** Both hostnames end up serving the app
 * directly, so the site answers on each rather than one sending browsers to the
 * other. A real 301 needs a Traefik middleware, and the deployment engine's
 * domain API exposes host, path, port, HTTPS and certificate type — there is no
 * redirect to ask it for. Said plainly here because "add the other one" and
 * "redirect the other one" look identical from the outside and are not.
 *
 * Null for a subdomain that is not `www`: `api.example.com` has no counterpart
 * anybody expects, and offering `www.api.example.com` would be noise.
 */
export function partnerHost(host: string): string | null {
  const clean = host.trim().toLowerCase();
  if (!clean) return null;
  if (isApex(clean)) return `www.${clean}`;
  return clean.startsWith("www.") ? clean.slice(4) : null;
}
